import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import UserSettingsLayout from '../../../components/UserSettingsLayout';
import ContentBox from '../../../components/ContentBox';

const API_URL = process.env.REACT_APP_API_URL;

const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    const d = new Date(dateString);
    if (isNaN(d)) return 'Invalid date';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

function InventoryCard({ item }) {
    const redeemStatus = () => {
        if (!item.isRedeemable) return null;
        if (item.isRedeemed) {
            return (
                <div className="space-y-1">
                    <span
                        className="inline-block bg-gray-200 text-gray-500 font-['Jersey_10'] uppercase text-[13px] tracking-[1px] px-3 py-1 w-full text-center"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.1)' }}
                    >
                        Redeemed
                    </span>
                    {item.redeemedAt && (
                        <p className="text-xs text-gray-400 text-center">{formatDate(item.redeemedAt)}</p>
                    )}
                </div>
            );
        }
        return (
            <button
                className="hover:opacity-90 text-white font-['Jersey_10'] uppercase text-[13px] tracking-[1px] px-3 py-1 w-full transition-opacity"
                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.25)', backgroundColor: '#5A2929' }}
            >
                Redeem
            </button>
        );
    };

    return (
        <div
            className="bg-white border border-gray-300 flex flex-col overflow-hidden"
            style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.15)' }}
        >
            <div className="w-full flex items-center justify-center bg-gray-100 border-b border-gray-200 h-[110px]">
                {item.imageURL ? (
                    <img
                        src={item.imageURL}
                        alt={item.itemName}
                        className="max-h-full max-w-full object-contain"
                        style={{ imageRendering: 'pixelated' }}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center gap-1 text-gray-400">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="2" y="7" width="20" height="14" rx="2" />
                            <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                        </svg>
                        <span className="font-['Jersey_10'] text-[12px] uppercase tracking-[1px]">No Image</span>
                    </div>
                )}
            </div>

            <div className="flex flex-col flex-1 p-3 gap-2">
                <h3 className="font-['Jersey_10'] uppercase text-[16px] tracking-[1px] text-gray-900 leading-tight">
                    {item.itemName}
                </h3>

                {item.itemDescription && (
                    <p className="text-xs text-gray-500 leading-snug flex-1">
                        {item.itemDescription}
                    </p>
                )}

                <div className="mt-auto space-y-2">
                    <p className="text-xs text-gray-400">
                        Acquired {formatDate(item.acquiredAt)}
                    </p>
                    {redeemStatus()}
                </div>
            </div>
        </div>
    );
}

function Inventory({ userData }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userData) return;
        load();
    }, [userData]);

    const load = async () => {
        try {
            const response = await fetch(`${API_URL}inventory/mine`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setItems(data.inventory);
            } else {
                toast.error('Failed to load inventory');
            }
        } catch {
            toast.error('Could not connect to server');
        } finally {
            setLoading(false);
        }
    };

    if (!userData) {
        return <Navigate to="/" replace />;
    }

    return (
        <UserSettingsLayout>
            <ContentBox theme="default" tint="lightskyblue" header="Inventory">
                {loading ? (
                    <p className="text-gray-600 font-['Jersey_10'] text-[16px]">Loading...</p>
                ) : items.length === 0 ? (
                    <p className="text-gray-600 font-['Jersey_10'] text-[16px]">
                        Your inventory is empty.
                    </p>
                ) : (
                    <>
                        <p className="text-sm text-gray-400 mb-4">
                            {items.length} item{items.length !== 1 ? 's' : ''}
                        </p>

                        <div
                            className="grid gap-4"
                            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}
                        >
                            {items.map(item => (
                                <InventoryCard key={item.inventoryId} item={item} />
                            ))}
                        </div>
                    </>
                )}
            </ContentBox>
        </UserSettingsLayout>
    );
}

export default Inventory;
